import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DeadlineDateCalcualator } from '@zaoblako/shared/common/classes/deadline.date.calcualator';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { EavAttributeFindInterface } from '@zaoblako/shared/modules/eav/dtos/attribute/eav-attribute-find.dto';
import { EavAttributeValueTypeEnum } from '@zaoblako/shared/modules/eav/enums/eav-attribute-value-type.enum';
import { HelpdeskNotificationSettingsFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-notification-settings.dto';
import type { HelpdeskTaskAuditorUpdateInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-auditor-update.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import {
  HelpdeskTaskFormDto,
  type HelpdeskTaskFormInterface
} from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-form.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import type { HelpdeskTaskTypeListQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-list-query.dto';
import type { HelpdeskTaskTemplateFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/template/helpdesk-task-template-find.dto';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { HelpdeskRequiredFieldsFindInterface } from '@zaoblako/shared/modules/saas/dtos/helpdesk-required-fields/helpdesk.required.fields.find.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file-find.dto';
import { ClientService } from '@zaoblako/web/core/services/client/client.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DealService } from '@zaoblako/web/core/services/deal/deal.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FileService } from '@zaoblako/web/core/services/file.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { HelpDeskSettingsService } from '@zaoblako/web/core/services/helpdesk/help-desk-settings.service';
import { HelpDeskTaskTemplateService } from '@zaoblako/web/core/services/helpdesk/help-desk-task-template.service';
import { HelpDeskTaskTypeService } from '@zaoblako/web/core/services/helpdesk/help-desk-task-type.service';
import { HelpDeskTaskService } from '@zaoblako/web/core/services/helpdesk/help-desk-task.service';
import { ProjectService } from '@zaoblako/web/core/services/project/project.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { TemplateEntityVariableService } from '@zaoblako/web/core/services/template-entity-variable.service';
import { FilesListComponent } from '@zaoblako/web/shared/components/files-list/files-list.component';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { ProjectAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/project-auto-complete/project-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { DaterangepickerComponent } from '@zaoblako/web/shared/dumbs/daterangepicker/daterangepicker.component';
import { ExternalUserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/external-user-multi-select/external-user-multi-select.component';
import { ExternalUserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/external-user-single-select/external-user-single-select.component';
import { CostCenterMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-multi-select/cost-center-multi-select.component';
import { CostCenterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-single-select/cost-center-single-select.component';
import { FilialMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-multi-select/filial-multi-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { HdTaskTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/hd-task-type-single-select/hd-task-type-single-select.component';
import { MasterMilestoneSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/master-milestone-single-select/master-milestone-single-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { HelpdeskTaskPriorityNamePipe } from '@zaoblako/web/shared/pipes/helpdesk-task-priority-name.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import moment from 'moment-timezone';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { DialogService } from 'primeng/dynamicdialog';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { forkJoin, Observable, Subscription } from 'rxjs';
import '@zaoblako/shared/modules/security/dtos/room-access/room-access-find.dto';
import '@zaoblako/shared/modules/messenger/dtos/thread/thread-find.dto';
import { EntityValidatorClass } from '@zaoblako/shared/modules/eav/classes/entity-validator.class';

@Component({
  standalone: true,
  selector: 'crm-helpdesk-task-form',
  templateUrl: './helpdesk-task-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    JoinPipe,
    ExternalUserSingleSelectComponent,
    ButtonModule,
    SingleSelectComponent,
    HdTaskTypeSingleSelectComponent,
    InputNumberModule,
    FormsModule,
    EditorComponent,
    MultiSelectComponent,
    ProjectAutoCompleteComponent,
    DatepickerComponent,
    HelpdeskTaskPriorityNamePipe,
    SpinnerComponent,
    DealAutoCompleteComponent,
    DividerModule,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    UserSingleSelectComponent,
    UserMultiSelectComponent,
    MasterMilestoneSingleSelectComponent,
    ExternalUserMultiSelectComponent,
    CostCenterSingleSelectComponent,
    CheckboxModule,
    InputTextComponent,
    DaterangepickerComponent,
    FileUploadModule,
    FilesListComponent,
    FilialMultiSelectComponent,
    CostCenterMultiSelectComponent,
    OrderByPipe
  ],
  providers: [DialogService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HelpdeskTaskFormComponent
  extends BaseFormComponent<HelpdeskTaskFormInterface>
  implements OnInit, OnDestroy
{
  @Input() public readonly index: number;
  public task = <HelpdeskTaskFormInterface>{};
  public dto = HelpdeskTaskFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;
  public engine: 'class-transformer' | 'serde' = 'serde';

  public clients: ClientFindInterface[] = [];
  public users: UserFindInterface[] = [];
  public taskTypes: HelpdeskTaskTypeFindInterface[] = [];
  public userGroups: UserGroupFindInterface[] = [];
  public requiredFields: HelpdeskRequiredFieldsFindInterface;
  public settings: HelpdeskNotificationSettingsFindInterface;

  public externalUsers: ExternalUserFindInterface[] = [];
  public auditors: HelpdeskTaskAuditorUpdateInterface[] = [];
  public priorities = Object.keys(HelpdeskTaskPriorityEnum);

  public period: {
    from: Date;
    to: Date;
  } = { from: null, to: null };

  @ViewChild('fileUpload') public fileUpload: FileUpload;
  public filesLoading = false;

  public get EavAttributeValueTypeEnum() {
    return EavAttributeValueTypeEnum;
  }

  private formSubscription$: Subscription = new Subscription();

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly helpdeskTaskService: HelpDeskTaskService,
    private readonly clientService: ClientService,
    private readonly dealService: DealService,
    private readonly userService: UserService,
    private readonly userGroupService: UserGroupService,
    private readonly projectService: ProjectService,
    private readonly helpDeskTaskTypeService: HelpDeskTaskTypeService,
    private readonly helpDeskTaskTemplateService: HelpDeskTaskTemplateService,
    private readonly templateEntityVariableService: TemplateEntityVariableService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    public readonly globalService: GlobalService,
    public readonly helpDeskSettingsService: HelpDeskSettingsService,
    private readonly fileService: FileService
  ) {
    super();
  }

  public ngOnInit(): void {
    const projectId = this.formStoreService.getState.forms[this.index]?.data?.projectId;
    const dealId = this.formStoreService.getState.forms[this.index]?.data?.dealId;
    const templateId = this.formStoreService.getState.forms[this.index]?.data?.templateId;
    const clientId = this.formStoreService.getState.forms[this.index]?.data?.clientId;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (!isNaN(projectId)) {
      this.projectService.findById<ProjectFindInterface>(projectId).subscribe((project) => {
        this.task.project = project;
        this.task.client = project.client;
        this.task.deadline = project.deadline;
        this.task.unit = project.unit;
        this.task.masterMilestone = project.masterMilestone;
        this.cdRef.markForCheck();
        this.loadTaskTypes();
      });
    }

    if (!isNaN(dealId)) {
      this.dealService.findById<DealFindInterface>(dealId).subscribe((deal) => {
        this.task.deal = deal;
        this.cdRef.markForCheck();
      });
    }

    if (!isNaN(templateId)) {
      this.helpDeskTaskTemplateService.findById<HelpdeskTaskTemplateFindInterface>(templateId).subscribe((template) => {
        this.task = {
          client: template.client,
          externalUser: template.externalUser,
          project: template.project,
          deal: template.deal,
          responsible: template.responsible,
          taskType: template.taskType,
          unit: template.unit,
          description: this.templateEntityVariableService.parse(template.description),
          userAuditors: template.userAuditors,
          userGroupAuditors: template.userGroupAuditors,
          plannedMin: template.plannedMin,
          masterMilestone: template.masterMilestone,
          deadline: DeadlineDateCalcualator.getInstance().calculateDate(
            template.deadline,
            template.planning.nearestDate || template.planning.runDate
          )
        };
        this.cdRef.markForCheck();
        this.loadTaskTypes();
      });
    }

    forkJoin([
      this.clientService.findAll({ select: ['id', 'name', 'contragents', 'externalUsers'] }),
      this.userService.findAll(),
      this.userGroupService.find<UserGroupFindInterface>(),
      this.helpdeskTaskService.getRequiredFields(),
      this.helpDeskSettingsService.getNotificationSettings()
    ]).subscribe(([clients, users, userGroups, requiredFields, settings]) => {
      if (!isNaN(clientId) && !this.formStoreService.getState.forms[this.index]?.isEdit) {
        this.task.client = clients.find((client) => client.id === clientId);
      }
      if (this.task.client?.externalUsers?.length) {
        this.externalUsers = this.task.client.externalUsers;
      }
      this.loadTaskTypes();
      this.clients = clients;
      this.users = users;
      this.userGroups = userGroups;
      this.requiredFields = requiredFields;
      this.settings = settings;
      this.cdRef.markForCheck();

      if (!this.formStoreService.getState.forms[this.index]?.isEdit) {
        this.helpDeskSettingsService.getDefaultClient().subscribe((defaultClient) => {
          if (!this.task.client) {
            this.task.client = clients.find((client) => client.id === defaultClient?.id);
            this.cdRef.markForCheck();
          }
        });
      }

      if (this.formStoreService.getState.forms[this.index]?.isEdit) {
        this.getById();
      }
    });
  }

  public ngOnDestroy(): void {
    this.formSubscription$?.unsubscribe();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.helpdeskTaskService
      .findById<HelpdeskTaskFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (task) => {
          this.task = task;
          if (this.task.client) {
            this.task.client = this.clients.find((client) => client.id === this.task.client?.id);
          }
          if (this.task.eavEntity?.values !== null && this.task.eavEntity?.values !== undefined) {
            Object.values(this.task.eavEntity.values).forEach((value) => {
              if (typeof value === 'object' && value !== null) {
                if ('from' in value) {
                  this.period.from = moment(value.from).toDate();
                }
                if ('to' in value) {
                  this.period.to = moment(value.to).toDate();
                }
              }
            });
          }
          if (this.task.client?.externalUsers?.length) {
            this.externalUsers = this.task.client.externalUsers;
          }
          this.task.userAuditors?.forEach((user) => {
            this.auditors.push({ user: user });
          });
          this.task.userGroupAuditors?.forEach((userGroup) => {
            this.auditors.push({ group: userGroup });
          });
          this.loadTaskTypes();
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveTask() {
    if (
      this.task?.taskType?.entityType?.attributes?.length &&
      this.task?.eavEntity?.values
    ) {
      const validator = EntityValidatorClass
        .getInstance()
        .createSafeSyncValidator(this.task.taskType.entityType.attributes, {
          hdTaskStatus: (this.task as HelpdeskTaskFindInterface)?.status,
          transformValidationErrors: (values) => ({ eavEntity: { values } }),
          transformValidationMetadata: (nested) => ({
            eavEntity: {
              label: 'Дополнительные реквизиты',
              nested: {
                values: {
                  label: 'Значения',
                  nested
                }
              }
            }
          })
        });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, errors] = validator(this.task.eavEntity.values);
      if (errors?.validationErrors) {
        this.errors = errors.validationErrors as any;
        console.error(this.errors);
        return;
      }
    }
    if (this.checkValidate(this.task).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.helpdeskTaskService.update<HelpdeskTaskFormInterface, HelpdeskTaskFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Заявка №${task?.id} обновлена`);
            this.formStoreService.saveNext(task, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.helpdeskTaskService.create<HelpdeskTaskFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Заявка создана');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/helpdesk/task/card/' + task.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(task, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteTask() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить заявку №${this.formStoreService.getState.forms[this.index]?.entityId}?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.helpdeskTaskService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(
              `Заявка №${this.formStoreService.getState.forms[this.index]?.entityId} удалена`
            );
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }

  public selectedClient(client: ClientFindInterface) {
    this.task.externalUser = null;
    this.task.taskType = null;
    this.cdRef.markForCheck();

    this.loadTaskTypes();

    if (client) {
      this.externalUsers = client.externalUsers;
    } else {
      this.externalUsers = [];
    }
    this.cdRef.markForCheck();
  }

  public openFormCreateExternalUser() {
    const result = this.formStoreService.addForm(TypeFormEnum.EXTERNAL_USER_CREATE, {
      headerText: 'Добавление внешнего пользователя',
      widthSize: 'form-sidebar-small',
      data: {
        client: this.task?.client,
        contragents: this.task?.client?.contragents
      }
    });

    this.formSubscription$.add(
      result.onSave.subscribe((externalUser: ExternalUserFindInterface) => {
        if (externalUser) {
          this.externalUsers.push(externalUser);
          this.externalUsers = [...this.externalUsers];
          this.task.externalUser = externalUser;
          this.cdRef.markForCheck();
        }
      })
    );
  }

  public setDefaultPriorityAndDeadline(taskType: HelpdeskTaskTypeFindInterface) {
    if (this.settings?.useTaskTypeDefaults && !this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.task.priority = null;
      this.task.deadline = null;
      if (taskType) {
        if (taskType.defaultPriority) {
          this.task.priority = taskType.defaultPriority;
        }
        if (taskType.defaultDeadline) {
          this.task.deadline = moment().add(taskType.defaultDeadline, 'day').toDate();
        }
      }
    }
  }

  private loadTaskTypes() {
    let queryParams: HelpdeskTaskTypeListQueryInterface = {};
    if (this.task?.client) {
      queryParams.clientIds = [this.task?.client?.id];
    }
    this.helpDeskTaskTypeService.find<HelpdeskTaskTypeFindInterface>(queryParams).subscribe({
      next: (taskTypes) => {
        this.taskTypes = taskTypes;
        if (this.task.taskType) {
          this.task.taskType = taskTypes.find((type) => type?.id === this.task.taskType?.id);
        }
        this.cdRef.markForCheck();
      }
    });
  }

  public setDefaultResponsibleAndUserGroups(taskType: HelpdeskTaskTypeFindInterface) {
    if (!this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.task.responsible = null;
      this.task.userGroupAuditors = [];
      if (taskType?.responsible) {
        this.task.responsible = taskType.responsible;
      }
      if (taskType?.userGroupAuditors?.length) {
        this.task.userGroupAuditors = taskType.userGroupAuditors;
      }
    }
  }

  public setEntityType(taskType: HelpdeskTaskTypeFindInterface) {
    if (!taskType.entityType?.attributes?.length) {
      this.task.eavEntity = null;
      this.period = { from: null, to: null };
      return;
    }

    const values: Record<number, any> = taskType.entityType.attributes.reduce(
      (acc: Record<number, any>, attribute: EavAttributeFindInterface) => {
        acc[attribute.id] = this.task.eavEntity?.values ? this.task.eavEntity.values?.[attribute.id] : null;
        return acc;
      },
      {}
    );
    this.task.eavEntity = { values };
    this.cdRef.markForCheck();
  }

  public uploadFiles(data: { files: FileList }, attribute: EavAttributeFindInterface) {
    this.filesLoading = true;
    this.cdRef.markForCheck();

    this.fileService.upload(data.files).subscribe({
      next: (files) => {
        this.task.eavEntity.values[attribute.id] = this.task.eavEntity.values[attribute.id]?.length
          ? this.task.eavEntity.values[attribute.id].concat(files)
          : files;
        this.fileUpload.clear();
        this.filesLoading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.filesLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public deleteFile(files: FileFindInterface[], file: FileFindInterface) {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить файл "${file.name}"?`,
      accept: () => {
        const index = files.indexOf(file);
        files.splice(index, 1);
        this.cdRef.markForCheck();
      }
    });
  }

  public setDates(dates: [Date, Date], attribute: EavAttributeFindInterface) {
    this.task.eavEntity.values[attribute.id] = {
      from: moment(dates[0]).format('YYYY-MM-DD'),
      to: moment(dates[1]).format('YYYY-MM-DD')
    };
  }

  public isRequiredField(attribute: EavAttributeFindInterface): boolean {
    return (
      this.formStoreService.getState.forms[this.index]?.isEdit &&
      attribute.requiredOnHdTaskStatuses
        ?.map(({ id }) => id)
        .includes((this.task as HelpdeskTaskFindInterface).status?.id)
    );
  }
}
